import React, { useEffect, useState } from 'react';
import { usePublicTrial } from 'src/contexts/PublicTrialContext';
import { getLogger } from 'src/utils/logger';
import flowerLogo from '../../assets/gymea-flower-only.png';
import './DocumentDownload.css';

const logger = getLogger('DocumentDownload');

export type DocumentType = 'behaviour-support-plan' | 'behaviour-response-plan' | 'plasp' | 'lesson_plan' | 'unit_of_work' | 'worksheet' | 'slideshow';

interface DocumentDownloadProps {
  base64Content?: string;
  downloadUrl?: string;
  documentId: string;
  documentType?: DocumentType;
  onClose?: () => void;
  onDownload?: () => void;
  downloadLabel?: string;
  onGenerateSlides?: () => void;
  onGenerateWorksheets?: () => void;
}

// Helper function to get the filename based on document type
const getFilename = (documentType: DocumentType, documentId: string): string => {
  switch (documentType) {
    case 'behaviour-support-plan':
      return `behaviour-support-plan-${documentId}.docx`;
    case 'behaviour-response-plan':
      return `behaviour-response-plan-${documentId}.docx`;
    case 'plasp':
      return `personalised-learning-support-plan-${documentId}.docx`;
    case 'lesson_plan':
      return `lesson-plan-${documentId}.docx`;
    case 'unit_of_work':
      return `unit-of-work-${documentId}.docx`;
    case 'worksheet':
      return `worksheet-${documentId}.docx`;
    case 'slideshow':
      return `slideshow-${documentId}.pptx`;
    default:
      return `document-${documentId}.docx`;
  }
};

// Helper function to get the display name for the document type
const getDocumentDisplayName = (documentType: DocumentType): string => {
  switch (documentType) {
    case 'behaviour-support-plan':
      return 'Behaviour Support Plan';
    case 'behaviour-response-plan':
      return 'Behaviour Response Plan';
    case 'plasp':
      return 'Personalised Learning and Support Plan';
    case 'lesson_plan':
      return 'Lesson Plan';
    case 'unit_of_work':
      return 'Unit of Work';
    case 'worksheet':
      return 'Student Quiz';
    case 'slideshow':
      return 'Slideshow';
    default:
      return 'Document';
  }
};

/**
 * A component that displays a document download for base64 encoded documents.
 * Supports Word documents by providing a modern download option.
 */
export const DocumentDownload: React.FC<DocumentDownloadProps> = ({
  base64Content,
  downloadUrl: propDownloadUrl,
  documentId,
  documentType = 'behaviour-support-plan', // Default to maintain backwards compatibility
  onClose,
  onDownload,
  downloadLabel = 'Download Document',
  onGenerateSlides,
  onGenerateWorksheets
}) => {
  const { isPublicTrial, requestTrialSignup } = usePublicTrial();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      logger.info(`Preparing download for document: ${documentId}`);

      // If we have a pre-signed download URL, use it directly
      if (propDownloadUrl) {
        setDownloadUrl(propDownloadUrl);
        setIsLoading(false);
        logger.info("Using pre-signed download URL");
        return;
      }

      // Fall back to base64 content processing for backward compatibility
      if (!base64Content) {
        setError('No document content or download URL available');
        setIsLoading(false);
        return;
      }

      try {
        // Create a blob from the base64 content
        const binaryString = window.atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Determine MIME type based on document type
        const mimeType = documentType === 'slideshow'
          ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        const blob = new Blob([bytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);

        logger.info("Download URL created successfully from base64");
        setIsLoading(false);
      } catch (error) {
        logger.error("Error converting base64 to blob:", error);
        setError('Failed to process document data');
        setIsLoading(false);
      }
    } catch (err) {
      logger.error('Error preparing document download:', err);
      setError('Failed to prepare document for download');
      setIsLoading(false);
    }
  }, [base64Content, propDownloadUrl, documentId, documentType]);

  const handleDownload = () => {
    logger.info("Download requested for document ID:", documentId);

    if (isPublicTrial) {
      requestTrialSignup();
      return;
    }

    if (onDownload) {
      onDownload();
    } else if (downloadUrl) {
      // Create a download link
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Always set download attribute to trigger download behavior
      link.download = getFilename(documentType, documentId);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      logger.info("Document download initiated");
    }
  };

  const documentDisplayName = getDocumentDisplayName(documentType);
  const isSlideshow = documentType === 'slideshow';
  const headingText = isSlideshow ? 'Your slides are ready!' : 'Your document is ready!';

  return (
    <div className="document-download-container">
      <div className="document-download-content">
        {isLoading ? (
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <p>Preparing your document...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">!</div>
            <h3>We encountered an issue</h3>
            <p>{error}</p>
          </div>
        ) : (
          <div className="success-container">
            <img src={flowerLogo} alt="Gymea Edu" className="success-logo" />
            <h3>{headingText}</h3>
            {!isSlideshow && (
              <p>Your {documentDisplayName} has been successfully created and is ready to download.</p>
            )}
            <div className="document-actions">
              <button
                className="download-button"
                onClick={handleDownload}
              >
                {downloadLabel}
              </button>
              {onGenerateSlides && (
                <button
                  className="download-button"
                  onClick={onGenerateSlides}
                >
                  Download {documentDisplayName.toLowerCase()} and generate slideshow
                </button>
              )}
              {onGenerateWorksheets && (
                <button
                  className="download-button"
                  onClick={onGenerateWorksheets}
                >
                  Download {documentDisplayName.toLowerCase()} and generate worksheets
                </button>
              )}
              {onClose && (
                <button className="close-button" onClick={onClose}>
                  Close
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
